import { usePlatform } from "@/platform/context"
import { cn } from "@/lib/utils"
import { useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router"
import {
	isReactNodeFrameMessage,
	reactNodeFrameHeight,
	reactNodeSandboxDocument,
} from "./react-node-runtime"

const ReactNodeError = ({ error }: { error: unknown }) => (
	<div
		role="alert"
		className="max-w-[360px] px-2 py-3 text-xs leading-relaxed text-red-700 dark:text-red-300"
	>
		{error instanceof Error ? error.message : String(error)}
	</div>
)

export const ReactNodeRenderer = ({
	code,
	nodeID,
	props,
	className,
}: {
	code: string
	nodeID: string
	props: Record<string, unknown>
	className?: string
}) => {
	const platform = usePlatform()
	const { projectId, shareToken } = useParams<{
		projectId?: string
		shareToken?: string
	}>()
	const frameRef = useRef<HTMLIFrameElement>(null)
	const channel = useMemo(() => crypto.randomUUID(), [code, nodeID])
	const nonce = useMemo(
		() => crypto.randomUUID().replaceAll("-", ""),
		[channel],
	)
	const [document, setDocument] = useState<string>()
	const [error, setError] = useState<unknown>()
	const [height, setHeight] = useState(48)

	useEffect(() => {
		if ((!projectId && !shareToken) || !platform.getReactNodeBundle) {
			setError(new Error("React node bundling is unavailable"))
			return
		}
		let active = true
		setError(undefined)
		setDocument(undefined)
		void platform
			.getReactNodeBundle(projectId ?? "", nodeID, shareToken, code)
			.then((bundle) => {
				if (!active) return
				setDocument(reactNodeSandboxDocument(channel, bundle.javascript, nonce))
			})
			.catch((caught) => {
				if (active) setError(caught)
			})
		return () => {
			active = false
		}
	}, [channel, code, nodeID, nonce, platform, projectId, shareToken])

	useEffect(() => {
		const receive = (event: MessageEvent) => {
			if (
				event.source !== frameRef.current?.contentWindow ||
				!isReactNodeFrameMessage(event.data, channel)
			) {
				return
			}
			if (event.data.kind === "ready") {
				frameRef.current?.contentWindow?.postMessage(
					{ channel, kind: "render", props },
					"*",
				)
			}
			if (event.data.kind === "resize") {
				setHeight(reactNodeFrameHeight(event.data.height))
			}
			if (event.data.kind === "error") {
				setError(new Error(String(event.data.message ?? "React node failed")))
			}
		}
		window.addEventListener("message", receive)
		return () => window.removeEventListener("message", receive)
	}, [channel, props])

	useEffect(() => {
		frameRef.current?.contentWindow?.postMessage(
			{ channel, kind: "render", props },
			"*",
		)
	}, [channel, props])

	return (
		<div className={cn("nodrag nopan nowheel min-h-12 w-full", className)}>
			{error ? <ReactNodeError error={error} /> : null}
			{!error && !document ? (
				<div className="px-2 py-3 text-xs text-muted-foreground">Bundling…</div>
			) : null}
			{document ? (
				<iframe
					ref={frameRef}
					title="React node"
					sandbox="allow-scripts"
					srcDoc={document}
					className={cn("block w-full border-0", Boolean(error) && "hidden")}
					style={{ height }}
				/>
			) : null}
		</div>
	)
}
